import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface GalleryImage {
  id: number;
  url: string;
  category: string;
  size?: 'large' | 'normal';
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

function CommercialJsonLd() {
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${SITE_URL}/portfolio/commercial#collection`,
      name: 'Commercial Photography | KAZU.PHOTO – 石川県金沢市 商業撮影ポートフォリオ',
      description: '石川県金沢市のフォトグラファーKAZU.PHOTOによる商業撮影ポートフォリオ。商品撮影・フード・空間・ブランド撮影。ブランドの佇まいまで伝わる写真を提供。',
      url: `${SITE_URL}/portfolio/commercial`,
      inLanguage: 'ja',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'ホーム', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Portfolio', item: `${SITE_URL}/#portfolio` },
          { '@type': 'ListItem', position: 3, name: 'Commercial', item: `${SITE_URL}/portfolio/commercial` },
        ],
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'jsonld-commercial';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById('jsonld-commercial');
      if (existing) existing.parentNode?.removeChild(existing);
    };
  }, []);

  return null;
}

export default function CommercialPage() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const images: GalleryImage[] = [
    {
      id: 1,
      url: 'https://public.readdy.ai/ai/img_res/44c71c6b2e7c8c6c371790692761f695.jpg',
      category: 'Product',
      size: 'normal'
    },
    {
      id: 2,
      url: 'https://readdy.ai/api/search-image?query=elegant%20japanese%20restaurant%20interior%20space%20photography%20with%20soft%20natural%20light%20minimal%20design%20wooden%20elements%20quiet%20atmosphere%20architectural%20detail%20refined%20aesthetic&width=1200&height=800&seq=comm2&orientation=landscape',
      category: 'Space',
      size: 'large'
    },
    {
      id: 3,
      url: 'https://static.readdy.ai/image/bbbff43ff07d452d166ab0b0917c289c/80958e5bad016161d8d7e1b8773da40f.jpeg',
      category: 'Food',
      size: 'normal'
    },
    {
      id: 4,
      url: 'https://readdy.ai/api/search-image?query=minimal%20brand%20lifestyle%20photography%20showing%20artisan%20product%20in%20natural%20environment%20soft%20muted%20tones%20elegant%20composition%20quiet%20refined%20aesthetic%20editorial%20style&width=800&height=1000&seq=comm4&orientation=portrait',
      category: 'Branding',
      size: 'normal'
    },
    {
      id: 5,
      url: 'https://readdy.ai/api/search-image?query=elegant%20minimalist%20product%20photography%20of%20handcrafted%20wooden%20furniture%20detail%20with%20soft%20natural%20light%20neutral%20background%20quiet%20refined%20aesthetic&width=800&height=1000&seq=comm5&orientation=portrait',
      category: 'Product',
      size: 'normal'
    },
    {
      id: 6,
      url: 'https://static.readdy.ai/image/bbbff43ff07d452d166ab0b0917c289c/a798e026000e6c7f6fdf716f458f8c1b.jpeg',
      category: 'Food',
      size: 'normal'
    },
    {
      id: 7,
      url: 'https://readdy.ai/api/search-image?query=minimal%20hotel%20room%20interior%20space%20photography%20with%20soft%20morning%20light%20clean%20lines%20neutral%20tones%20quiet%20elegant%20atmosphere%20architectural%20detail&width=800&height=1000&seq=comm7&orientation=portrait',
      category: 'Space',
      size: 'normal'
    },
    {
      id: 8,
      url: 'https://readdy.ai/api/search-image?query=artisan%20brand%20photography%20showing%20craftsperson%20hands%20working%20with%20traditional%20materials%20soft%20natural%20light%20documentary%20style%20quiet%20refined%20aesthetic&width=800&height=1000&seq=comm8&orientation=portrait',
      category: 'Branding',
      size: 'normal'
    },
    {
      id: 9,
      url: 'https://readdy.ai/api/search-image?query=elegant%20minimalist%20beauty%20product%20photography%20with%20natural%20botanical%20elements%20soft%20diffused%20light%20neutral%20background%20quiet%20refined%20composition&width=800&height=1000&seq=comm9&orientation=portrait',
      category: 'Product',
      size: 'normal'
    }
  ];

  const handleContactClick = () => {
    window.REACT_APP_NAVIGATE('/');
    setTimeout(() => {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      <CommercialJsonLd />
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-light text-gray-900 mb-6 tracking-tight">
            Commercial
          </h1>
          <p className="text-lg text-gray-600 font-light tracking-wide">
            ブランドの物語
          </p>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-12 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-base leading-relaxed text-gray-700 font-light">
            商品そのものだけでなく、<br />
            その背景にある空気や、ブランドの佇まいまで伝わる写真を目指しています。
          </p>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xl md:text-2xl font-light text-gray-800 tracking-wide">
            商品は、背景ごと記憶される。
          </p>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {images.map((image) => (
              <div
                key={image.id}
                className={`relative group cursor-pointer overflow-hidden ${
                  image.size === 'large' ? 'md:col-span-2 md:row-span-1' : ''
                }`}
                onClick={() => setSelectedImage(image.url)}
              >
                <div className="relative w-full" style={{ paddingBottom: image.size === 'large' ? '66.67%' : '125%' }}>
                  <img
                    src={image.url}
                    alt={`Commercial photography - ${image.category}`}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />
                </div>
                <div className="mt-3">
                  <span className="text-xs text-gray-500 font-light tracking-wider uppercase">
                    {image.category}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="space-y-3">
            <p className="text-base text-gray-700 font-light">商品撮影</p>
            <p className="text-base text-gray-700 font-light">飲食・フード</p>
            <p className="text-base text-gray-700 font-light">宿泊施設・空間</p>
            <p className="text-base text-gray-700 font-light">工芸・ブランド</p>
            <p className="text-base text-gray-700 font-light">店舗やサービスの世界観撮影</p>
          </div>
        </div>
      </section>

      {/* Inquiry Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-light text-gray-900 mb-6 tracking-tight">
            商業撮影のご相談
          </h2>
          <p className="text-base text-gray-700 mb-10 font-light leading-relaxed">
            商品、空間、ブランドの世界観を写真で伝えたい方は、<br />
            お気軽にご相談ください。
          </p>
          <button
            onClick={handleContactClick}
            className="inline-block px-10 py-4 bg-gray-900 text-white text-sm tracking-wider hover:bg-gray-800 transition-colors duration-300 whitespace-nowrap cursor-pointer"
          >
            お問い合わせはこちら
          </button>
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-6 right-6 text-white text-4xl hover:text-gray-300 transition-colors w-12 h-12 flex items-center justify-center cursor-pointer"
            onClick={() => setSelectedImage(null)}
          >
            <i className="ri-close-line"></i>
          </button>
          <img
            src={selectedImage}
            alt="Selected"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}